import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { herdBaseKey } from '@/lib/queries';

interface BulkPreview {
  animal_count: number;
  animals: Array<{
    animal_id: string;
    name: string | null;
    current_status: string;
    current_barn_group: string | null;
  }>;
  proposed_changes: Record<string, string>;
}

interface BulkUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animalIds: string[];
  updateType: 'group' | 'status';
  onSuccess: () => void;
}

export function BulkUpdateDialog({
  open,
  onOpenChange,
  animalIds,
  updateType,
  onSuccess,
}: BulkUpdateDialogProps) {
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [confirmToken, setConfirmToken] = useState('');
  const queryClient = useQueryClient();

  function reset() {
    setValue('');
    setPreview(null);
    setConfirmToken('');
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      const updates = updateType === 'group' ? { barn_group: value } : { status: value };
      const res = await api.post<{ status: string; preview: BulkPreview; confirm_token: string }>(
        '/api/herd/bulk',
        { animal_ids: animalIds, updates }
      );
      return res;
    },
    onSuccess: (data) => {
      if (data.status === 'preview') {
        setPreview(data.preview);
        setConfirmToken(data.confirm_token);
      }
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const updates = updateType === 'group' ? { barn_group: value } : { status: value };
      return api.post('/api/herd/bulk', {
        animal_ids: animalIds,
        updates,
        confirm_token: confirmToken,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: herdBaseKey });
      onOpenChange(false);
      reset();
      onSuccess();
    },
  });

  function handleClose() {
    onOpenChange(false);
    reset();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{updateType === 'group' ? 'Move to Group' : 'Change Status'}</DialogTitle>
          <DialogDescription>
            {preview
              ? `Review changes for ${preview.animal_count} animals`
              : `Update ${animalIds.length} selected animal${animalIds.length !== 1 ? 's' : ''}`}
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <div className="space-y-2">
              {updateType === 'group' ? (
                <>
                  <Label>New Group Name</Label>
                  <Input
                    placeholder="e.g. East Barn"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <Label>New Status</Label>
                  <Select value={value} onValueChange={setValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status…" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Milking', 'Dry', 'Close-up', 'Heifer', 'Calf'].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => previewMutation.mutate()}
                disabled={!value || previewMutation.isPending}
              >
                Preview Changes
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg bg-ndd-900/50 p-3">
              {preview.animals.map((a) => (
                <div key={a.animal_id} className="flex items-center justify-between text-sm">
                  <span className="text-white">
                    {a.animal_id} {a.name && `— ${a.name}`}
                  </span>
                  <span className="text-ndd-400 text-xs">
                    {updateType === 'group' ? (a.current_barn_group ?? '—') : a.current_status}
                    {' → '}
                    <span className="text-white">{value}</span>
                  </span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back
              </Button>
              <Button onClick={() => confirmMutation.mutate()} disabled={confirmMutation.isPending}>
                Confirm Update
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
